import * as THREE from 'three';

export const PRESETS_BASE = '/presets/';

// HDRI окружения - пути к локальным .hdr файлам
// Поместите свои HDRI файлы в папку /public/hdri/
export const HDRI_MAPS: Record<string, string> = {
  'night': '/hdri/rogland_clear_night_2k.hdr',
  'photostudio': '/hdri/brown_photostudio_02_2k.hdr',
  'daylight': '/hdri/studio_small.hdr',
};

// Паттерны для автоопределения PBR карт по имени файла
export const PBR_MAP_PATTERNS: Record<string, RegExp[]> = {
  color: [/color/i, /albedo/i, /diffuse/i, /basecolor/i, /base_color/i],
  normal: [/normal/i, /nrm/i, /nor/i],
  roughness: [/roughness/i, /rough/i, /rgh/i],
  ao: [/ao/i, /ambient/i, /occlusion/i, /cavity/i],
  metalness: [/metal/i, /metallic/i, /metalness/i],
};

// Определяет тип PBR карты по имени файла
export function detectMapType(filename: string): string | null {
  const lowerName = filename.toLowerCase();
  
  // Пропускаем displacement (слишком тяжелый для real-time)
  if (/displacement/i.test(lowerName) || /height/i.test(lowerName) || /disp/i.test(lowerName)) {
    return null;
  }
  // Пропускаем opacity (прозрачность обрабатывается отдельно)
  if (/opacity/i.test(lowerName) || /alpha/i.test(lowerName)) {
    return null;
  }
  
  for (const [mapType, patterns] of Object.entries(PBR_MAP_PATTERNS)) {
    for (const pattern of patterns) {
      if (pattern.test(lowerName)) {
        return mapType;
      }
    }
  }
  return null;
}

// Автоматически находит все PBR карты в папке по базовому имени файла
export async function autoDetectPBRMaps(baseURL: string, knownFiles?: string[]): Promise<Record<string, string>> {
  const maps: Record<string, string> = {};
  
  if (knownFiles) {
    // Если список файлов известен, используем его
    for (const file of knownFiles) {
      const mapType = detectMapType(file);
      if (mapType && !maps[mapType]) {
        maps[mapType] = file;
      }
    }
  }
  
  return maps;
}

export function getElementById(id: string): HTMLElement {
  const element = document.getElementById(id);
  if (!element) {
    throw new Error(`Element with id "${id}" not found`);
  }
  return element;
}

export function colorToHex(color: THREE.Color | null): string {
  if (!color) return '#FFFFFF';
  return '#' + color.getHexString().toUpperCase();
}

export function ensurePBR(material: THREE.Material | THREE.Material[]): THREE.Material | THREE.Material[] {
  if (Array.isArray(material)) {
    return material.map(mat => ensurePBR(mat) as THREE.Material);
  }

  if (!material) return material;

  const isPBR = material instanceof THREE.MeshStandardMaterial || 
                material instanceof THREE.MeshPhysicalMaterial;
  
  if (!isPBR) {
    const newMaterial = new THREE.MeshStandardMaterial();
    if ((material as any).normalMap) (newMaterial as any).normalMap = (material as any).normalMap;
    (newMaterial as any).color.copy((material as any).color || new THREE.Color(0xffffff));
    newMaterial.needsUpdate = true;
    return newMaterial;
  }

  material.needsUpdate = true;
  return material;
}

export function setTextureProperties(
  texture: THREE.Texture | null,
  tiling: number,
  rotation: number
): void {
  if (!texture) return;
  
  texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
  texture.center.set(0.5, 0.5);
  texture.repeat.set(tiling, tiling);
  texture.rotation = rotation;
  texture.needsUpdate = true;
}

export async function fetchJSON(url: string): Promise<any> {
  const response = await fetch(url, { credentials: 'same-origin' });
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }
  return response.json();
}

export async function loadRemoteTexture(url: string, colorSpace?: THREE.ColorSpace): Promise<THREE.Texture | null> {
  return new Promise((resolve) => {
    if (!url) return resolve(null);
    
    const loader = new THREE.TextureLoader();
    loader.setCrossOrigin('anonymous');
    loader.load(
      url,
      (texture) => {
        // Важно: THREE.NoColorSpace может быть пустой строкой, поэтому проверяем на undefined
        if (colorSpace !== undefined) texture.colorSpace = colorSpace;
        resolve(texture);
      },
      undefined,
      () => resolve(null)
    );
  });
}

export function downloadJSON(obj: any, filename: string): void {
  const a = document.createElement('a');
  a.download = filename;
  a.href = 'data:application/json;charset=utf-8,' + encodeURIComponent(JSON.stringify(obj, null, 2));
  a.click();
}

export function centerAndWrap(root: THREE.Object3D): { wrapper: THREE.Group; sizeY: number } {
  const box = new THREE.Box3().setFromObject(root);
  const center = box.getCenter(new THREE.Vector3());
  const size = box.getSize(new THREE.Vector3());
  
  root.position.sub(center);
  
  const wrapper = new THREE.Group();
  wrapper.add(root);
  
  return { wrapper, sizeY: size.y > 0 ? size.y : 1 };
}

export function updateSpotLight(light: THREE.SpotLight): void {
  if (!light?.shadow?.camera) return;
  light.shadow.camera.fov = THREE.MathUtils.radToDeg(light.angle);
  light.shadow.camera.updateProjectionMatrix();
}

// Экспорт FABP формата
export * from './FabpFormat';
